import * as THREE from 'three';
import { isFlightMode } from '../camera/CameraManager.js';
import { GAME_HUD } from '../core/SimConfig.js';

const NM_PER_METER = 1 / 1852;

const _ndc = new THREE.Vector3();
const _camForward = new THREE.Vector3();

/**
 * Screen-space HUD overlay. A single 2D canvas sibling to the WebGL canvas
 * with mix-blend-mode: plus-lighter for true additive compositing against the
 * rendered scene. Draws orange target diamonds + distance readouts around
 * enemies while the player holds the highlight control (Space), plus cyan navigation
 * markers for the active training-mission waypoint (always visible, since a
 * "fly here" objective is useless without one).
 */
export default class GameHUD {
  constructor({ aircraft, controller, cameraManager, enemyManager, camera, trainingMission = null }) {
    this.aircraft = aircraft;
    this.controller = controller;
    this.cameraManager = cameraManager;
    this.enemyManager = enemyManager;
    this.camera = camera;
    // Source of navigation waypoints (null outside flight-school scenarios).
    this.trainingMission = trainingMission;
    this._time = 0;

    const canvas = document.createElement('canvas');
    canvas.id = 'game-hud';
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this._visible = true;

    this._onResize = this._onResize.bind(this);
    this._onResize();
    window.addEventListener('resize', this._onResize);

    document.getElementById('app').appendChild(canvas);
  }

  setVisible(v) {
    this._visible = v;
    this.canvas.style.display = v ? '' : 'none';
  }

  update(dt = 0) {
    if (!this._visible) return;
    const ctx = this.ctx;
    const w = window.innerWidth;
    const h = window.innerHeight;
    ctx.clearRect(0, 0, w, h);
    this._time += dt;

    // Camera forward — shared by the behind-camera culls below.
    _camForward.set(0, 0, -1).applyQuaternion(this.camera.quaternion);

    if (this._isFlightView()) this._drawWaypoints(w, h);
    if (this._isFlightView() && this.controller?.mouseStickActive) this._drawStick(w, h);

    if (!this._shouldShowTargets()) return;

    const enemies = this.enemyManager?.entities;
    if (!enemies || enemies.length === 0) return;

    const cfg = GAME_HUD.target;
    const playerPos = this.aircraft.position;
    const camPos = this.camera.position;
    const maxD = cfg.maxDistance;
    const sz = cfg.diamondSize;
    const minA = cfg.minAlpha;

    ctx.lineWidth = 2;
    ctx.strokeStyle = cfg.color;
    ctx.fillStyle = cfg.color;
    ctx.font = cfg.labelFont;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (e.dead || e.destroyed) continue;
      const pos = e.flight?.position;
      if (!pos) continue;

      // Distance from the A-10 (player aircraft, not camera)
      const dx = pos.x - playerPos.x;
      const dy = pos.y - playerPos.y;
      const dz = pos.z - playerPos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist > maxD) continue;

      // Behind-camera cull (world-space dot vs camera forward)
      const cdx = pos.x - camPos.x;
      const cdy = pos.y - camPos.y;
      const cdz = pos.z - camPos.z;
      if (cdx * _camForward.x + cdy * _camForward.y + cdz * _camForward.z <= 0) continue;

      // Project to NDC, then to logical pixels
      _ndc.copy(pos).project(this.camera);
      if (_ndc.z >= 1 || _ndc.x < -1.05 || _ndc.x > 1.05 || _ndc.y < -1.05 || _ndc.y > 1.05) continue;

      const sx = Math.round((_ndc.x * 0.5 + 0.5) * w);
      const sy = Math.round((1 - (_ndc.y * 0.5 + 0.5)) * h);

      // Distance-based alpha (fades linearly toward minA at maxD)
      const t = dist / maxD;
      ctx.globalAlpha = minA + (1 - minA) * (1 - t);

      ctx.beginPath();
      ctx.moveTo(sx, sy - sz);
      ctx.lineTo(sx + sz, sy);
      ctx.lineTo(sx, sy + sz);
      ctx.lineTo(sx - sz, sy);
      ctx.closePath();
      ctx.stroke();

      const nm = dist * NM_PER_METER;
      ctx.fillText(`${nm.toFixed(1)} NM`, sx, sy + sz + 4);
    }

    ctx.globalAlpha = 1;
  }

  /**
   * Navigation markers for the active training waypoint(s). A waypoint inside
   * the frustum gets a ticked circle with its name above and slant range below;
   * one behind or off the edges is pinned to a margin inset from the screen
   * border as a chevron aimed at it, so the player always knows which way to
   * turn. Breathes gently so it reads against terrain and cloud.
   */
  _drawWaypoints(w, h) {
    const list = this.trainingMission?.getHudWaypoints();
    if (!list || list.length === 0) return;

    const cfg = GAME_HUD.waypoint;
    const camPos = this.camera.position;
    const playerPos = this.aircraft.position;
    const m = cfg.edgeMargin;
    const pulse = 1 - cfg.pulseDepth * 0.5 * (1 - Math.cos(this._time * (Math.PI * 2) / cfg.pulsePeriod));

    const ctx = this.ctx;
    ctx.lineWidth = cfg.lineWidth;
    ctx.strokeStyle = cfg.color;
    ctx.fillStyle = cfg.color;
    ctx.textAlign = 'center';

    for (const wp of list) {
      const dist = Math.hypot(wp.x - playerPos.x, wp.y - playerPos.y, wp.z - playerPos.z);
      const range = dist < 1000
        ? `${Math.round(dist / 10) * 10} M`
        : `${(dist * NM_PER_METER).toFixed(1)} NM`;

      const behind = (wp.x - camPos.x) * _camForward.x
        + (wp.y - camPos.y) * _camForward.y
        + (wp.z - camPos.z) * _camForward.z <= 0;

      _ndc.set(wp.x, wp.y, wp.z).project(this.camera);
      // Projection mirrors the point when it's behind the camera — flip it back
      // so the edge chevron points the short way round.
      let nx = behind ? -_ndc.x : _ndc.x;
      let ny = behind ? -_ndc.y : _ndc.y;

      ctx.globalAlpha = pulse;

      const extent = Math.max(Math.abs(nx), Math.abs(ny));
      if (behind || extent > 1) {
        // Push the direction out onto the unit square, then inset by the margin.
        if (extent > 1e-4) { nx /= extent; ny /= extent; }
        const sx = Math.min(w - m, Math.max(m, (nx * 0.5 + 0.5) * w));
        const sy = Math.min(h - m, Math.max(m, (1 - (ny * 0.5 + 0.5)) * h));
        this._drawWaypointArrow(sx, sy, Math.atan2(-ny, nx), cfg, wp, range);
      } else {
        const sx = Math.round((nx * 0.5 + 0.5) * w);
        const sy = Math.round((1 - (ny * 0.5 + 0.5)) * h);
        this._drawWaypointMarker(sx, sy, cfg, wp, range);
      }
    }

    ctx.globalAlpha = 1;
  }

  _drawWaypointMarker(sx, sy, cfg, wp, range) {
    const ctx = this.ctx;
    const r = cfg.radius;

    ctx.beginPath();
    ctx.arc(sx, sy, r, 0, Math.PI * 2);
    ctx.stroke();

    // Four diagonal ticks radiating from the ring — reads as a nav fix rather
    // than a target box or a gun reticle.
    const k = Math.SQRT1_2;
    ctx.beginPath();
    for (const [dx, dy] of [[k, k], [-k, k], [k, -k], [-k, -k]]) {
      ctx.moveTo(sx + dx * r, sy + dy * r);
      ctx.lineTo(sx + dx * (r + cfg.tick), sy + dy * (r + cfg.tick));
    }
    ctx.stroke();

    if (wp.label) {
      ctx.font = cfg.labelFont;
      ctx.textBaseline = 'bottom';
      ctx.fillText(wp.label, sx, sy - r - cfg.tick - 4);
    }
    ctx.font = cfg.rangeFont;
    ctx.textBaseline = 'top';
    ctx.fillText(range, sx, sy + r + cfg.tick + 4);
  }

  _drawWaypointArrow(sx, sy, angle, cfg, wp, range) {
    const ctx = this.ctx;
    const a = cfg.arrowSize;

    ctx.save();
    ctx.translate(sx, sy);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(a * 1.4, 0);
    ctx.lineTo(-a * 0.7, a * 0.85);
    ctx.lineTo(-a * 0.7, -a * 0.85);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();

    // Range stays upright, parked on the inboard side of the chevron — i.e.
    // back along the direction the chevron points, toward screen centre.
    ctx.font = cfg.rangeFont;
    ctx.textBaseline = 'middle';
    ctx.fillText(range, sx - Math.cos(angle) * (a * 2.6), sy - Math.sin(angle) * (a * 2.6));
  }

  dispose() {
    window.removeEventListener('resize', this._onResize);
    this.canvas.remove();
  }

  _shouldShowTargets() {
    return !!this.controller?.highlightTargets && this._isFlightView();
  }

  /** Manual-mouse virtual stick: ring at bottom centre, dot at the stick position. */
  _drawStick(w, h) {
    const cfg = GAME_HUD.stick;
    const ctx = this.ctx;
    const c = this.controller;
    const cx = w / 2, cy = h - cfg.bottomMargin - cfg.radius;
    const mag = Math.min(1, Math.hypot(c.stickX, c.stickY));
    ctx.globalAlpha = cfg.alphaIdle + (cfg.alphaActive - cfg.alphaIdle) * mag;
    ctx.strokeStyle = cfg.color;
    ctx.fillStyle = cfg.color;
    ctx.lineWidth = cfg.lineWidth;
    ctx.beginPath();
    ctx.arc(cx, cy, cfg.radius, 0, Math.PI * 2);
    ctx.stroke();
    // Stick back (pull) draws the dot low, like the stick's own travel.
    const dx = cx + c.stickX * cfg.radius;
    const dy = cy + c.stickY * cfg.radius;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(dx, dy);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(dx, dy, cfg.dotRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  /** True while the active (or incoming) camera is one the player is flying
   *  from — chase, cockpit, or an action mount. */
  _isFlightView() {
    const cm = this.cameraManager;
    if (!cm) return false;
    return isFlightMode(cm._transitioning ? cm._toMode : cm.mode);
  }

  _onResize() {
    // Cap DPR — anything above 2× is wasted on a HUD overlay and bloats fill cost.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    // Draw in logical-pixel coordinates; the transform handles DPR scaling.
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
}
